import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  CalendarDays, Loader2, MapPin, ExternalLink, Plus, Pencil, Trash2, Sparkles, Building2, Radar,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ALL = '__all__';

const CAT_COLORS = {
  conference: 'bg-sky-100 text-sky-700',
  expo: 'bg-emerald-100 text-emerald-700',
  awards: 'bg-amber-100 text-amber-700',
  webinar: 'bg-violet-100 text-violet-700',
  training: 'bg-slate-100 text-slate-700',
};
const fmtDate = (s) => {
  if (!s) return 'TBC';
  try { return new Date(s).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); }
  catch { return s; }
};
const EMPTY = { name: '', organiser: '', category: 'conference', url: '', start_date: '', end_date: '', location: '', format: 'in_person', audiences: [], notes: '' };

const Events = () => {
  const { user } = useAuth();
  const isAdmin = user?.is_admin || user?.role === 'owner' || user?.role === 'admin';

  const [events, setEvents] = useState([]);
  const [meta, setMeta] = useState({ audiences: [], categories: [] });
  const [loading, setLoading] = useState(true);
  const [audience, setAudience] = useState(ALL);
  const [upcoming, setUpcoming] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [editing, setEditing] = useState(null); // event obj or {} for new
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [crawling, setCrawling] = useState(false);
  const [crawlStatus, setCrawlStatus] = useState(null);

  const audienceLabel = (v) => meta.audiences.find((a) => a.value === v)?.label || v;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (audience !== ALL) params.audience = audience;
      if (upcoming) params.upcoming = true;
      const [evRes, metaRes] = await Promise.all([
        axios.get(`${API}/events`, { params }),
        axios.get(`${API}/events/meta`),
      ]);
      setEvents(evRes.data.events || []);
      setMeta(metaRes.data);
      try { const cs = await axios.get(`${API}/events/crawl-status`); setCrawlStatus(cs.data); } catch { /* noop */ }
    } catch { toast.error('Failed to load events'); }
    finally { setLoading(false); }
  }, [audience, upcoming]);

  useEffect(() => { load(); }, [load]);

  const runCrawl = async () => {
    setCrawling(true);
    try {
      await axios.post(`${API}/events/discover`);
      toast.info('Crawling care-sector listings for new 2026/2027 events…');
      // poll status until done
      for (let i = 0; i < 12; i++) {
        await new Promise((r) => setTimeout(r, 8000));
        const { data } = await axios.get(`${API}/events/crawl-status`);
        setCrawlStatus(data);
        if (data.status === 'done') {
          toast.success(`Crawl complete - ${data.added} new events added (${data.sources_ok} sources)`);
          load();
          break;
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Crawl failed');
    } finally { setCrawling(false); }
  };

  const seed = async () => {
    setSeeding(true);
    try {
      const { data } = await axios.post(`${API}/events/seed-defaults`);
      toast.success(`Seeded ${data.created} events (total ${data.total})`);
      load();
    } catch { toast.error('Seed failed (admin only)'); }
    finally { setSeeding(false); }
  };

  const openNew = () => { setForm(EMPTY); setEditing({}); };
  const openEdit = (ev) => { setForm({ ...EMPTY, ...ev }); setEditing(ev); };

  const toggleAudience = (val) => {
    setForm((f) => ({ ...f, audiences: f.audiences.includes(val) ? f.audiences.filter((a) => a !== val) : [...f.audiences, val] }));
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Name required'); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      if (editing.id) await axios.put(`${API}/events/${editing.id}`, payload);
      else await axios.post(`${API}/events`, payload);
      toast.success('Saved');
      setEditing(null);
      load();
    } catch { toast.error('Save failed (admin only)'); }
    finally { setSaving(false); }
  };

  const del = async (ev) => {
    if (!window.confirm(`Delete "${ev.name}"?`)) return;
    try { await axios.delete(`${API}/events/${ev.id}`); toast.success('Deleted'); load(); }
    catch { toast.error('Delete failed'); }
  };

  return (
    <div className="space-y-6" data-testid="events-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <CalendarDays className="w-6 h-6 text-[#00aeef]" /> Sector Events Calendar
          </h1>
          <p className="text-gray-500 text-sm mt-1">Major UK care-sector conferences, expos &amp; awards - tagged by audience. Plan campaigns, webinars &amp; sponsorships.</p>
        </div>
        {isAdmin && (
          <div className="flex gap-2">
            {events.length === 0 && (
              <Button variant="outline" onClick={seed} disabled={seeding} data-testid="events-seed-btn">
                {seeding ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}Seed known events
              </Button>
            )}
            <Button variant="outline" onClick={runCrawl} disabled={crawling} data-testid="events-crawl-btn">
              {crawling ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Radar className="w-4 h-4 mr-2" />}Discover events
            </Button>
            <Button onClick={openNew} className="bg-[#00aeef] hover:bg-[#0097cf]" data-testid="events-add-btn"><Plus className="w-4 h-4 mr-2" />Add event</Button>
          </div>
        )}
      </div>

      {crawlStatus && crawlStatus.last_run_at && (
        <p className="text-xs text-gray-400" data-testid="events-crawl-status">
          Monthly auto-crawl · last run {new Date(crawlStatus.last_run_at).toLocaleString('en-GB')} · {crawlStatus.added ?? 0} added from {crawlStatus.sources_ok ?? 0} sources · covers 2026-2027
        </p>
      )}

      <Card><CardContent className="p-4 flex flex-wrap gap-3 items-center">
        <Select value={audience} onValueChange={setAudience}>
          <SelectTrigger className="w-52" data-testid="events-audience-filter"><SelectValue placeholder="Audience" /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All audiences</SelectItem>
            {meta.audiences.map((a) => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant={upcoming ? 'default' : 'outline'} size="sm" onClick={() => setUpcoming(!upcoming)} data-testid="events-upcoming-toggle">
          {upcoming ? 'Upcoming only' : 'All dates'}
        </Button>
      </CardContent></Card>

      {loading ? (
        <div className="p-12 text-center text-gray-400"><Loader2 className="w-6 h-6 animate-spin inline" /></div>
      ) : events.length === 0 ? (
        <Card><CardContent className="p-10 text-center text-gray-500">No events yet. {isAdmin && 'Click "Seed known events" to load the major UK care-sector events.'}</CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {events.map((ev) => (
            <Card key={ev.id} className="hover:shadow-md transition-shadow" data-testid={`event-card-${ev.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge className={`text-xs capitalize ${CAT_COLORS[ev.category] || 'bg-gray-100 text-gray-700'}`}>{ev.category}</Badge>
                      <span className="text-sm font-semibold text-[#0077b6]">{fmtDate(ev.start_date)}{ev.end_date && ev.end_date !== ev.start_date ? ` - ${fmtDate(ev.end_date)}` : ''}</span>
                      {ev.source === 'crawl' && <Badge variant="outline" className="text-[10px] text-gray-400 border-gray-300" title="Auto-discovered by the monthly crawl">Auto</Badge>}
                    </div>
                    <h3 className="font-semibold text-gray-900">{ev.name}</h3>
                    <div className="text-xs text-gray-500 mt-1 flex flex-wrap gap-x-3 gap-y-1">
                      {ev.organiser && <span className="flex items-center gap-1"><Building2 className="w-3 h-3" />{ev.organiser}</span>}
                      {ev.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{ev.location}</span>}
                      {ev.format && <span className="capitalize">{ev.format.replace('_', '-')}</span>}
                    </div>
                  </div>
                  {isAdmin && (
                    <div className="flex gap-1">
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEdit(ev)} data-testid={`event-edit-${ev.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-500" onClick={() => del(ev)} data-testid={`event-delete-${ev.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  )}
                </div>
                {ev.notes && <p className="text-xs text-gray-500 mt-2">{ev.notes}</p>}
                <div className="flex flex-wrap gap-1 mt-2">
                  {(ev.audiences || []).map((a) => <Badge key={a} variant="outline" className="text-[10px]">{audienceLabel(a)}</Badge>)}
                </div>
                {ev.url && <a href={ev.url} target="_blank" rel="noreferrer" className="text-xs text-[#0077b6] hover:underline inline-flex items-center gap-1 mt-2"><ExternalLink className="w-3 h-3" />Event site</a>}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="event-form-dialog">
          <DialogHeader>
            <DialogTitle>{editing?.id ? 'Edit event' : 'Add event'}</DialogTitle>
            <DialogDescription>Care-sector event details. Dates can be updated as they're confirmed.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="event-name-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Organiser</Label><Input value={form.organiser || ''} onChange={(e) => setForm({ ...form, organiser: e.target.value })} /></div>
              <div><Label>Location</Label><Input value={form.location || ''} onChange={(e) => setForm({ ...form, location: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Start date</Label><Input type="date" value={form.start_date || ''} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="event-start-input" /></div>
              <div><Label>End date</Label><Input type="date" value={form.end_date || ''} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{meta.categories.map((c) => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Format</Label>
                <Select value={form.format} onValueChange={(v) => setForm({ ...form, format: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="in_person">In-person</SelectItem>
                    <SelectItem value="virtual">Virtual</SelectItem>
                    <SelectItem value="hybrid">Hybrid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Event URL</Label><Input value={form.url || ''} onChange={(e) => setForm({ ...form, url: e.target.value })} /></div>
            <div>
              <Label>Audiences</Label>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {meta.audiences.map((a) => (
                  <button type="button" key={a.value} onClick={() => toggleAudience(a.value)}
                    className={`text-xs px-2.5 py-1 rounded-full border ${form.audiences.includes(a.value) ? 'bg-[#00aeef] text-white border-[#00aeef]' : 'border-gray-300 text-gray-600'}`}>
                    {a.label}
                  </button>
                ))}
              </div>
            </div>
            <div><Label>Notes</Label><Textarea rows={2} value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-[#00aeef] hover:bg-[#0097cf]" data-testid="event-save-btn">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Events;
